/**
 * Platform · Staff calls.
 *
 * Everybody on staff calls, as a directory to ring from: each person's
 * face, trade, department and level, whether they are online now and on
 * what, and two buttons — Voice and Video. A button the policy refuses stays
 * on the card, greyed, with the reason written under it, because a button
 * that silently vanishes reads as a fault and one that explains itself
 * reads as a rule. Several people can be ticked and rung together as a
 * group, up to the policy's limit.
 *
 * Calling "using the system's stored contact info" also means the telephone:
 * each person's stored numbers are tel: links beside the in-app buttons, and
 * the contact directory's people who have no sign-in — a contractor's
 * supervisor, the Civil Defence duty officer — are listed as phone only.
 *
 * v2 adds, beside all of that: a "Meetings" card (live calls to join or ask
 * into, upcoming and past meetings, schedule or meet now); "Ringtone & Do not
 * disturb"; who can join a group call, on the group bar; and a person on Do
 * not disturb marked on their card — pressing Voice or Video then says first
 * that they will not ring, and offers an urgent call only to a level the
 * administrator lets break through.
 *
 * The call itself is not on this screen. It lives above every screen (the
 * shell's CallsProvider), so starting one here and walking off to a work
 * order keeps it going.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { useCallDirectory, useCallHistory, useCallPolicy } from "../../api/queries";
import { Button, Card, EmptyState, ErrorState, Input, Skeleton } from "../../design/components";
import {
  duration,
  historyOutcome,
  LEVEL_LABEL,
  meOn,
  OUTCOME_LABEL,
  presence,
  type Call,
  type CallMedia,
  type CallPerson,
  type CallStartOptions,
  type JoinRule,
} from "../../domain/calls";
import { locale, t, tn } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useCalls, type CallsApi } from "./CallsProvider";
import { CALL_ICONS, CallIcon, Face } from "./CallWindow";
import { dndUntilText } from "./CallsMenu";
import { JoinRuleEditor } from "./JoinRuleEditor";
import { MeetingsCard } from "./MeetingsCard";
import { RingtoneDndCard } from "./RingtoneDndCard";
import s from "./Calls.module.css";

export function CallsPage() {
  const calls = useCalls();
  const session = useSession();
  const eyebrow = t("{site} · Platform", { site: session.site.code });
  if (!calls || !calls.available) {
    return (
      <Page eyebrow={eyebrow} title="Staff calls">
        <EmptyState>
          {t("Staff calls are not open to this sign-in. An administrator can give your role the calls permission.")}
        </EmptyState>
      </Page>
    );
  }
  return <Directory calls={calls} eyebrow={eyebrow} />;
}

function Directory({ calls, eyebrow }: { calls: CallsApi; eyebrow: string }) {
  const dir = useCallDirectory();
  const history = useCallHistory();
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [now] = useState(() => Date.now());
  const summary = dir.data?.summary;
  const groupMax = summary?.groupMax ?? 6;
  const inCall = !!calls.state.call;
  /* may my level break through Do not disturb: the directory says, else the policy does */
  const policy = useCallPolicy(summary?.canOverrideDnd === undefined && !!summary);
  const canOverride = summary?.canOverrideDnd ?? (summary ? !!policy.data?.policy.dndOverride[summary.myLevel] : false);
  const [join, setJoin] = useState<JoinRule>({ mode: "invited", allow: [] });
  const [title, setTitle] = useState("");
  /* a call to somebody on Do not disturb is confirmed first */
  const [confirm, setConfirm] = useState<{ p: CallPerson; media: CallMedia } | null>(null);

  /* /calls#ringtone and #meetings, from the header menu and the reminders */
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    el?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [hash, dir.isPending]);

  /* opening this screen is seeing the missed calls */
  const { markSeen } = calls;
  useEffect(() => {
    markSeen();
    // once per visit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = dir.data?.items ?? [];
  const match = (v: (string | undefined)[]) => !q || v.some((x) => (x ?? "").toLowerCase().includes(q.toLowerCase()));
  const shown = items.filter((p) => match([p.name, p.jobTitle, p.department, ...p.phones.map((x) => x.value)]));
  const phoneOnly = (dir.data?.contacts ?? []).filter((c) =>
    match([c.name, c.org, c.role, ...c.phones.map((x) => x.value)]),
  );
  const online = items.filter((p) => p.online).length;
  const known = new Set(items.map((p) => p.principalId));
  const missedToday = (history.data ?? []).filter(
    (c) => historyOutcome(c, meOn(c, calls.state.me, known)) === "missed",
  ).length;

  useScreenContext({
    route: "/calls",
    title: "Staff calls",
    summary: [
      t("{n} colleagues on staff calls, {o} online now.", { n: items.length, o: online }),
      tn(missedToday, "{n} missed call in the recent list.", "{n} missed calls in the recent list."),
    ],
    offers: [{ label: t("Who can call whom?"), ask: "who can call whom on staff calls?" }],
    worksInFull: true,
  });

  const start = async (ids: string[], media: CallMedia, opts?: CallStartOptions) => {
    if (await calls.engine.call(ids, media, opts)) {
      setPicked([]);
      setTitle("");
    }
  };
  const startGroup = (media: CallMedia) =>
    void start(
      picked,
      media,
      picked.length > 1 ? { join, ...(title.trim() ? { title: title.trim() } : {}) } : undefined,
    );
  const startOne = (p: CallPerson, media: CallMedia) => {
    if (p.dnd) setConfirm({ p, media });
    else void start([p.principalId], media);
  };
  const toggle = (id: string) =>
    setPicked((x) => (x.includes(id) ? x.filter((y) => y !== id) : x.length >= groupMax - 1 ? x : [...x, id]));

  return (
    <Page eyebrow={eyebrow} title="Staff calls">
      <p className={s.lede}>
        {t(
          "Call colleagues by voice or video, one to one or in a group; the call stays on top while you work.",
        )}{" "}
        <Link to="/call-policy">{t("Who can call whom →")}</Link>
      </p>

      {calls.sample && (
        <div className={s.sample} data-testid="calls-sample">
          <span className={s.sampleTag}>{t("Sample staff")}</span>
          <span>
            {t(
              "These are demo people, not your colleagues. A stand-in answers, and the screen says nobody is on the line. Your camera and microphone are real if you allow them.",
            )}
          </span>
          {calls.simulateIncoming && calls.dndOn && (
            <span className={s.dim} data-testid="sample-dnd-hint">
              {t("You are on Do not disturb: a sample call will not ring — it is listed as missed.")}
            </span>
          )}
          {calls.simulateIncoming && (
            <span className={s.sampleActions}>
              <Button size="sm" onClick={() => void calls.simulateIncoming!("audio")} data-testid="try-incoming">
                {t("Try an incoming call")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void calls.simulateIncoming!("video")}
                data-testid="try-incoming-video"
              >
                {t("Try an incoming video call")}
              </Button>
            </span>
          )}
        </div>
      )}

      <div className={s.bar}>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("search a name, a trade, a department, a number")}
          aria-label={t("Search staff")}
          className={s.search}
        />
        <label className={s.check}>
          <input type="checkbox" checked={calls.ringMuted} onChange={(e) => calls.setRingMuted(e.target.checked)} />{" "}
          {t("Mute the ringtone")}
        </label>
        <NotificationsButton />
        <span className={s.count}>{t("{n} on staff calls · {o} online", { n: items.length, o: online })}</span>
      </div>

      {picked.length > 0 && (
        <div className={s.group} data-testid="group-bar">
          <b>{tn(picked.length, "{n} person chosen", "{n} people chosen")}</b>
          <span className={s.dim}>{t("A call holds up to {n} people, you included.", { n: groupMax })}</span>
          {summary && !summary.canGroup && picked.length > 1 ? (
            <span className={s.refused}>{t("Your level may not start group calls.")}</span>
          ) : (
            <>
              <Button
                variant="primary"
                disabled={inCall || calls.state.busy}
                onClick={() => startGroup("audio")}
                data-testid="group-voice"
              >
                {picked.length > 1 ? t("Group call (Voice)") : t("Voice")}
              </Button>
              <Button
                variant="primary"
                disabled={inCall || calls.state.busy || summary?.canVideo === false}
                onClick={() => startGroup("video")}
                data-testid="group-video"
              >
                {picked.length > 1 ? t("Group call (Video)") : t("Video")}
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={() => setPicked([])}>
            {t("Clear")}
          </Button>
          {picked.length > 1 && (!summary || summary.canGroup) && (
            <div className={s.groupMore} data-testid="group-join">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("A title for the call (optional)")}
                aria-label={t("Title")}
                maxLength={120}
                className={s.groupTitle}
              />
              <span className={s.groupJoinLabel}>{t("Who can join")}</span>
              <JoinRuleEditor value={join} onChange={setJoin} compact testId="group-join-rule" />
            </div>
          )}
          {picked.some((id) => items.find((p) => p.principalId === id)?.dnd) && (
            <span className={s.dndNote}>
              {t("Somebody chosen is on Do not disturb: they will not ring, and will see a missed call.")}
            </span>
          )}
        </div>
      )}
      {calls.state.error && !calls.state.call && (
        <p className={s.refused} role="alert">
          {calls.state.error}
        </p>
      )}

      <Card title="Colleagues" sub="Online first. Numbers dial from your device.">
        {dir.isPending ? (
          <Skeleton rows={6} />
        ) : dir.isError ? (
          <ErrorState error={dir.error} retry={() => void dir.refetch()} />
        ) : shown.length === 0 ? (
          <EmptyState>{q ? t("Nobody matches.") : t("Nobody else is on staff calls yet.")}</EmptyState>
        ) : (
          <div className={s.people}>
            {[...shown]
              .sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name))
              .map((p) => (
                <PersonCard
                  key={p.principalId}
                  p={p}
                  now={now}
                  picked={picked.includes(p.principalId)}
                  pickDisabled={!p.canCall || (!picked.includes(p.principalId) && picked.length >= groupMax - 1)}
                  toggle={() => toggle(p.principalId)}
                  start={(m) => startOne(p, m)}
                  busy={inCall || calls.state.busy}
                  levelVideo={summary?.canVideo !== false}
                />
              ))}
          </div>
        )}
      </Card>

      <MeetingsCard calls={calls} />

      <Card title="Recent calls" sub="Made, answered and missed (in red)">
        <Recent
          calls={calls}
          history={history.data}
          pending={history.isPending}
          known={new Set(items.map((p) => p.principalId))}
          start={(ids, m) => void start(ids, m)}
        />
      </Card>

      <RingtoneDndCard calls={calls} />

      <Card
        title="Phone only"
        sub="No platform sign-in; reach them by phone"
      >
        {phoneOnly.length === 0 ? (
          <EmptyState>{t("Nobody in the contact directory is phone only.")}</EmptyState>
        ) : (
          <ul className={s.phoneOnly} data-testid="phone-only">
            {phoneOnly.map((c) => (
              <li key={c.personId}>
                <div>
                  <b>{c.name}</b>
                  <span className={s.dim}>{[c.role, c.org].filter(Boolean).join(" · ")}</span>
                </div>
                <Phones phones={c.phones} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      {confirm && (
        <DndConfirm
          p={confirm.p}
          media={confirm.media}
          canOverride={canOverride}
          busy={calls.state.busy}
          close={() => setConfirm(null)}
          go={(urgent) => {
            const c = confirm;
            setConfirm(null);
            void start([c.p.principalId], c.media, urgent ? { urgent: true } : undefined);
          }}
        />
      )}
    </Page>
  );
}

/** Before ringing somebody on Do not disturb: say they will not ring, and offer what the policy allows. */
function DndConfirm({
  p,
  media,
  canOverride,
  busy,
  close,
  go,
}: {
  p: CallPerson;
  media: CallMedia;
  canOverride: boolean;
  busy: boolean;
  close: () => void;
  go: (urgent: boolean) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);
  const until = dndUntilText(p.dndUntil, !p.dndUntil);
  return (
    <div className={s.overlay} onClick={close}>
      <div
        className={s.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t("{name} is on Do not disturb", { name: p.name })}
        onClick={(e) => e.stopPropagation()}
        data-testid="dnd-confirm"
      >
        <div className={s.dialogHead}>
          <span className={s.dndMoonBig} aria-hidden="true">
            ☾
          </span>
          <div>
            <b>{t("{name} is on Do not disturb", { name: p.name })}</b>
            <span className={s.dim}>{until}</span>
          </div>
        </div>
        <p>
          {t(
            "Your call won't ring for them. They'll see a missed call when Do not disturb ends, and can call you back.",
          )}
        </p>
        {canOverride ? (
          <p className={s.dim}>
            {t("Your level may make an urgent call, which rings through Do not disturb. Keep it for what cannot wait.")}
          </p>
        ) : (
          <p className={s.dim}>{t("Your level cannot break through Do not disturb.")}</p>
        )}
        <div className={s.dialogButtons}>
          <Button variant="ghost" onClick={close}>
            {t("Cancel")}
          </Button>
          <Button disabled={busy} onClick={() => go(false)} data-testid="dnd-call-anyway">
            {media === "video" ? t("Video call anyway") : t("Call anyway")}
          </Button>
          {canOverride && (
            <Button variant="primary" disabled={busy} onClick={() => go(true)} data-testid="dnd-urgent">
              {t("Urgent call")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function PersonCard({
  p,
  now,
  picked,
  pickDisabled,
  toggle,
  start,
  busy,
  levelVideo,
}: {
  p: CallPerson;
  now: number;
  picked: boolean;
  pickDisabled: boolean;
  toggle: () => void;
  start: (m: CallMedia) => void;
  busy: boolean;
  levelVideo: boolean;
}) {
  const pr = presence(p, now);
  const reason = p.reason ? t(p.reason) : t("The policy does not allow this call.");
  const videoReason = !p.canCall ? reason : !p.canVideo || !levelVideo ? t("Your level cannot use video.") : undefined;
  return (
    <article className={s.person} data-testid={`person-${p.principalId}`} data-picked={picked}>
      <label className={s.pick} title={p.canCall ? t("Choose for a group call") : reason}>
        <input
          type="checkbox"
          checked={picked}
          disabled={pickDisabled}
          onChange={toggle}
          aria-label={t("Choose {name} for a group call", { name: p.name })}
        />
      </label>
      <span className={s.faceWrap}>
        <Face name={p.name} employeeId={p.employeeId} hasPhoto={p.hasPhoto} size={52} />
        <span className={`${s.presence} ${pr.online ? s.presenceOn : ""}`} aria-hidden="true" />
      </span>
      <div className={s.who}>
        <b>{p.name}</b>
        <span className={s.dim}>{[p.jobTitle, p.department].filter(Boolean).join(" · ")}</span>
        <span className={s.meta}>
          <span className={s.level}>{t(LEVEL_LABEL[p.level])}</span>
          <span className={pr.online ? s.onlineText : s.dim}>{t(pr.template, pr.vars)}</span>
          {p.dnd && (
            <span className={s.dndChip} data-testid={`dnd-${p.principalId}`}>
              ☾ {t("Do not disturb")} · {dndUntilText(p.dndUntil, !p.dndUntil)}
            </span>
          )}
        </span>
        <Phones phones={p.phones} />
      </div>
      <div className={s.buttons}>
        <Button
          onClick={() => start("audio")}
          disabled={!p.canCall || busy}
          title={
            !p.canCall
              ? reason
              : busy
                ? t("Finish the call you are on first.")
                : t("Voice call {name}", { name: p.name })
          }
          aria-label={t("Voice call {name}", { name: p.name })}
          data-testid={`voice-${p.principalId}`}
        >
          <CallIcon size={15} /> {t("Voice")}
        </Button>
        <Button
          onClick={() => start("video")}
          disabled={!!videoReason || busy}
          title={
            videoReason ?? (busy ? t("Finish the call you are on first.") : t("Video call {name}", { name: p.name }))
          }
          aria-label={t("Video call {name}", { name: p.name })}
          data-testid={`video-${p.principalId}`}
        >
          <CallIcon d={CALL_ICONS.cam} size={15} /> {t("Video")}
        </Button>
      </div>
      {!p.canCall && (
        <p className={s.refused} data-testid={`reason-${p.principalId}`}>
          {reason}
        </p>
      )}
    </article>
  );
}

function Phones({ phones }: { phones: { label: string; value: string; kind?: string }[] }) {
  if (!phones.length) return null;
  return (
    <span className={s.phones}>
      {phones.map((x) => (
        <a key={x.value} href={`tel:${x.value.replace(/[^\d+]/g, "")}`} title={t("Dial from this device")}>
          {x.label ? `${t(x.label)} ` : ""}
          {/* a number reads left to right in either language */}
          <bdi dir="ltr">{x.value}</bdi>
        </a>
      ))}
    </span>
  );
}

function Recent({
  calls,
  history,
  pending,
  known,
  start,
}: {
  calls: CallsApi;
  history: Call[] | undefined;
  pending: boolean;
  known: Set<string>;
  start: (ids: string[], m: CallMedia) => void;
}) {
  const rows = useMemo(() => (history ?? []).slice(0, 20), [history]);
  if (pending) return <Skeleton rows={3} />;
  if (!rows.length) return <EmptyState>{t("No calls yet.")}</EmptyState>;
  return (
    <ul className={s.recent} data-testid="recent-calls">
      {rows.map((c) => {
        const me = meOn(c, calls.state.me, known);
        const outcome = historyOutcome(c, me);
        const others = c.participants.filter((p) => p.principalId !== me);
        const otherIds = others.map((p) => p.principalId);
        const names = others.map((p) => p.name).join(", ") || c.startedByName || t("A colleague");
        const outgoing = c.startedBy === me;
        const talked =
          c.answeredAt && c.endedAt ? duration((Date.parse(c.endedAt) - Date.parse(c.answeredAt)) / 1000) : null;
        return (
          <li key={c.id} data-outcome={outcome}>
            <span className={outcome === "missed" ? s.missed : s.dim}>{outgoing ? "↗" : "↙"}</span>
            <div className={s.recentWho}>
              <b className={outcome === "missed" ? s.missed : undefined}>{names}</b>
              <span className={s.dim}>
                {c.media === "video" ? t("Video") : t("Voice")} ·{" "}
                {new Date(c.createdAt).toLocaleString(locale(), { dateStyle: "medium", timeStyle: "short" })}
                {talked && <> · {talked}</>}
              </span>
            </div>
            <span className={outcome === "missed" ? s.missedBadge : s.outcome}>{t(OUTCOME_LABEL[outcome])}</span>
            <Button
              size="sm"
              disabled={!otherIds.length || !!calls.state.call || calls.state.busy}
              onClick={() => start(otherIds, c.media)}
            >
              {t("Call back")}
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

/** Ask for notification permission only when the person presses for it — never at load. */
function NotificationsButton() {
  const supported = typeof Notification !== "undefined";
  const [perm, setPerm] = useState(() => (supported ? Notification.permission : "denied"));
  if (!supported) return null;
  if (perm === "granted") return <span className={s.dim}>{t("Call notifications are on")}</span>;
  if (perm === "denied")
    return <span className={s.dim}>{t("Call notifications are blocked in this browser's settings")}</span>;
  return (
    <Button
      size="sm"
      onClick={() =>
        void Notification.requestPermission()
          .then(setPerm)
          .catch(() => undefined)
      }
    >
      {t("Allow call notifications")}
    </Button>
  );
}
